import { useEffect, useState, useCallback, ReactNode } from "react";

export type Props = {
  /** Array of texts to rotate through */
  texts: string[];
  /** Interval duration in seconds between text changes */
  intervalDuration?: number;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Render prop for the text item */
  renderText?: (props: {
    text: string;
    index: number;
    isActive: boolean;
  }) => ReactNode;
  /** Callback when text changes */
  onTextChange?: (index: number, text: string) => void;
};

/**
 * Base rotating text component with no styling.
 * Cycles through an array of texts at a specified interval.
 * Uses render props for customizable text rendering and animations.
 */
export function RotatingTextBase({
  texts,
  intervalDuration = 5,
  className,
  "data-testid": testId = "rotating-text-base",
  renderText,
  onTextChange,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0);

  const advanceText = useCallback(() => {
    setActiveIndex((prev) => {
      const nextIndex = prev < texts.length - 1 ? prev + 1 : 0;
      onTextChange?.(nextIndex, texts[nextIndex]);
      return nextIndex;
    });
  }, [texts, onTextChange]);

  useEffect(() => {
    if (texts.length <= 1) return;

    const interval = setInterval(advanceText, intervalDuration * 1000);
    return () => clearInterval(interval);
  }, [texts.length, intervalDuration, advanceText]);

  if (texts.length === 0) return null;

  return (
    <span className={className} data-testid={testId} data-active-index={activeIndex}>
      {texts.map((text, index) => {
        const isActive = index === activeIndex;
        if (renderText) {
          return renderText({ text, index, isActive });
        }
        return isActive ? (
          <span key={text} data-testid={`${testId}-text-${index}`}>
            {text}
          </span>
        ) : null;
      })}
    </span>
  );
}

RotatingTextBase.displayName = "RotatingTextBase";

